import React, { Fragment, useState, useEffect, useContext, useRef } from 'react';
import Api from '../../../Api';
import Status from '../ProjectEditing/Status';
import { InputHelper } from '../../Utils/FieldsHelper';
import classes from './styles.module.css';
import MessageContext from '../../../MessageContext';
import { FormattedMessage, useIntl } from 'react-intl';
import UserSearchList from '../UserSearchList';
import TerminalEditing from '../TerminalEditing';
import PermissionsEditor from '../PermissionsEditor';

function ProjectEditing({ project, uuid, token, usersList, onProjectSaved: _onProjectSaved }) {
    const intl = useIntl();
    const [isSaving, setIsSaving] = useState(false);
    const [form, setForm] = useState({});
    const [fullMerchant, setFullMerchant] = useState(null);
    const [editFull, setEditFull] = useState(null);
    const [isFullLoading, setIsFullLoading] = useState(false);
    const [isAntifraud, setIsAntifraud] = useState(false);
    const [antifraudCheckAccess, setAntifraudCheckAccess] = useState(false);
    const [antifraudSettings, setAntifraudSettings] = useState({
        payment: { database: true, client: true, scanfraud: false },
        payout: { database: true, client: true, scanfraud: false },
    });

    // Widget settings removed - now stored per MerchantSettings in editFull.settings[].widget and editFull.settings[].widgetCallback

    const { onMessage } = useContext(MessageContext);
    const mounted = useRef(true);
    const onMessageRef = useRef(onMessage);
    const intlRef = useRef(intl);
    onMessageRef.current = onMessage;
    intlRef.current = intl;

    const [projectUsers, setProjectUsers] = useState([]);
    const [originalProjectUsers, setOriginalProjectUsers] = useState([]);
    const [terminalIdToTerminal, setTerminalIdToTerminal] = useState({});

    const [terminalModal, setTerminalModal] = useState({
        open: false,
        terminal: null,
        settingIndex: null,
    });

    const [permissionsModal, setPermissionsModal] = useState({
        open: false,
        user: null,
    });

    function onChange(name, value) {
        setForm((prev) => ({ ...prev, [name]: value }));
    }

    function setCurrentStatus(id) {
        setForm((prev) => ({ ...prev, status: id }));
    }

    useEffect(() => {
        mounted.current = true;

        setForm({
            status: project && project.status,
            name: (project && project.name) || '',
            description: (project && project.description) || '',
            url: (project && project.url) || '',
            inn: (project && project.inn) || '',
            kpp: (project && project.kpp) || '',
            ogrn: (project && project.ogrn) || '',
        });

        function deepClone(obj) {
            try {
                return JSON.parse(JSON.stringify(obj));
            } catch (e) {
                return null;
            }
        }

        const fetchFull = async () => {
            if (!uuid) {
                setFullMerchant(null);
                setEditFull(null);
                setProjectUsers([]);
                return;
            }

            try {
                setIsFullLoading(true);
                const api = new Api({ token });
                const data = await api.getFullMerchant(uuid);
                if (!mounted.current) return;

                setFullMerchant(data);
                setEditFull(deepClone(data));

                // Обновляем форму из полных данных мерчанта
                if (data.merchant) {
                    setForm((prev) => ({
                        ...prev,
                        name: data.merchant.name || prev.name || '',
                        description: data.merchant.description || prev.description || '',
                        url: data.merchant.url || prev.url || '',
                        inn: data.merchant.inn || prev.inn || '',
                        kpp: data.merchant.kpp || prev.kpp || '',
                        ogrn: data.merchant.ogrn || prev.ogrn || '',
                        status: data.merchant.status != null ? data.merchant.status : prev.status,
                    }));
                }

                setIsAntifraud(
                    data.settings.length > 0
                        ? !(data.settings[0].frod === null || data.settings[0].frod === false)
                        : false
                );
                setAntifraudCheckAccess(data.settings.length > 0 ? !!data.settings[0].antifraudCheckAccess : false);

                // Load antifraud category settings
                if (data.settings && data.settings.length > 0 && data.settings[0].antifraud) {
                    setAntifraudSettings(data.settings[0].antifraud);
                } else {
                    setAntifraudSettings({
                        payment: { database: true, client: true, scanfraud: false },
                        payout: { database: true, client: true, scanfraud: false },
                    });
                }

                // Widget settings are now loaded per MerchantSettings (stored in each settings[].widget and settings[].widgetCallback)

                const terminalMap = {};
                const terminals = (data && data.terminals) || [];
                if (Array.isArray(terminals)) {
                    terminals.forEach((ta) => {
                        if (ta && ta.id != null) {
                            terminalMap[ta.id] = ta;
                        }
                    });
                }
                setTerminalIdToTerminal(terminalMap);

                try {
                    const projectData = await api.getProjectData(uuid);
                    const existingUsers =
                        (projectData && Array.isArray(projectData.accounts) && projectData.accounts) || [];

                    if (mounted.current) {
                        setProjectUsers(existingUsers);
                        setOriginalProjectUsers(existingUsers);
                    }
                } catch (userErr) {
                    console.warn('Не удалось получить пользователей проекта:', userErr);
                }
            } catch (e) {
                onMessageRef.current({
                    type: 'error',
                    message: intlRef.current.formatMessage({ id: 'ProjectEditing.error.load' }),
                });
            } finally {
                if (mounted.current) setIsFullLoading(false);
            }
        };

        fetchFull();

        return () => {
            mounted.current = false;
        };
    }, [uuid, token]);

    // -------- helpers for diffing & payloads (без терминалов) --------
    function shallowEqual(a, b) {
        const ka = Object.keys(a || {});
        const kb = Object.keys(b || {});
        if (ka.length !== kb.length) return false;
        for (const k of ka) {
            if (a[k] !== b[k]) return false;
        }
        return true;
    }

    function isServerId(id) {
        return typeof id === 'number' && Number.isFinite(id);
    }

    function pickSettingPayload(s) {
        return {
            providerUuid: s && s.providerUuid ? s.providerUuid : null,
            terminalId: s && s.terminalId != null ? s.terminalId : null,
            enabled: !!s.enabled,
            priority: s && s.priority != null ? s.priority : 1,
            fee: (s && s.fee) || {},
            isSBP: !!s.isSBP,
            isMP2P: !!s.isMP2P,
            preAuth: !!s.preAuth,
            partialRefund: !!s.partialRefund,
            partialReversal: !!s.partialReversal,
            recurring: !!s.recurring,
            payoutBalanceCheck: !!s.payoutBalanceCheck,
            transactionRules: Array.isArray(s && s.transactionRules) ? s.transactionRules : [],
            widget: (s && s.widget) || '',
            widgetCallback: (s && s.widgetCallback) || '',
        };
    }

    async function saveAll() {
        setIsSaving(true);
        const api = new Api({ token });

        try {
            // Собираем все данные для единого запроса
            const payload = {
                // Базовые данные проекта
                basic: {
                    name: form.name,
                    description: form.description,
                    url: form.url,
                    status: form.status,
                    inn: form.inn,
                    kpp: form.kpp,
                    ogrn: form.ogrn,
                },
                // Настройки антифрода
                fraud: {
                    frod: isAntifraud,
                    antifraud: isAntifraud ? antifraudSettings : null,
                    antifraudCheckAccess: isAntifraud ? antifraudCheckAccess : false,
                },
                // Widget settings are now saved per MerchantSettings (in settings.update[].widget and settings.update[].widgetCallback)
            };

            // Вычисляем изменения пользователей
            const origAccountIds = new Set(originalProjectUsers.map((a) => a && a.id).filter(Boolean));
            const newAccountIds = new Set(projectUsers.map((u) => u && u.id).filter(Boolean));

            const usersToAdd = projectUsers.filter((u) => u && u.id && !origAccountIds.has(u.id)).map((u) => u.id);
            const usersToRemove = originalProjectUsers
                .filter((a) => a && a.id && !newAccountIds.has(a.id))
                .map((a) => a.id);

            if (usersToAdd.length > 0 || usersToRemove.length > 0) {
                payload.users = {
                    add: usersToAdd,
                    remove: usersToRemove,
                };
            }

            // Вычисляем изменения настроек (MID)
            if (fullMerchant && editFull) {
                const origSettings = Array.isArray(fullMerchant.settings) ? fullMerchant.settings : [];
                const newSettings = Array.isArray(editFull.settings) ? editFull.settings : [];

                const origById = new Map(origSettings.filter((s) => isServerId(s && s.id)).map((s) => [s.id, s]));

                const deleteSettingsIds = origSettings
                    .filter((s) => isServerId(s && s.id) && !newSettings.find((ns) => ns && ns.id === s.id))
                    .map((s) => s.id);

                const createSettings = [];
                const updateSettings = [];

                for (const s of newSettings) {
                    if (!isServerId(s && s.id)) {
                        createSettings.push(pickSettingPayload({ ...s, terminalId: null }));
                    } else {
                        const before = origById.get(s.id) || {};
                        const payloadBefore = pickSettingPayload(before);
                        const payloadNow = pickSettingPayload(s);
                        if (
                            !shallowEqual(
                                JSON.parse(JSON.stringify(payloadBefore)),
                                JSON.parse(JSON.stringify(payloadNow))
                            )
                        ) {
                            updateSettings.push({ id: s.id, ...payloadNow });
                        }
                    }
                }

                if (deleteSettingsIds.length > 0 || createSettings.length > 0 || updateSettings.length > 0) {
                    payload.settings = {
                        create: createSettings,
                        update: updateSettings,
                        delete: deleteSettingsIds,
                    };
                }
            }

            // Единый запрос на сохранение всех данных
            await api.saveProjectAll(uuid, payload);

            // Обновляем оригинальные данные пользователей после успешного сохранения
            setOriginalProjectUsers([...projectUsers]);

            // Обновляем данные (оверлей isSaving остаётся активным)
            await refreshFull(true);

            // Тост только после полного завершения сохранения и обновления
            onMessage({ type: 'success', message: intl.formatMessage({ id: 'ProjectEditing.success.allSaved' }) });
        } catch (e) {
            console.error(e);
            onMessage({ type: 'error', message: intl.formatMessage({ id: 'ProjectEditing.error.save' }) });
        } finally {
            setIsSaving(false);
        }
    }

    function updateAntifraud(antifraudValue) {
        setIsAntifraud(antifraudValue);
    }

    function updateAntifraudCategory(type, category, value) {
        setAntifraudSettings((prev) => ({
            ...prev,
            [type]: {
                ...prev[type],
                [category]: value,
            },
        }));
    }

    function generateRandomKey(length = 64) {
        const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
        let result = '';
        for (let i = 0; i < length; i++) {
            result += chars.charAt(Math.floor(Math.random() * chars.length));
        }
        return result;
    }

    const updateSetting = (index, patch) => {
        setEditFull((prev) => {
            const next = Object.assign({}, prev || {});
            const list = Array.isArray(next.settings) ? next.settings.slice() : [];
            const merged = Object.assign({}, list[index] || {}, patch || {});
            list[index] = merged;
            next.settings = list;
            return next;
        });
    };

    function generateWidgetKeyForSetting(idx) {
        const newKey = generateRandomKey(64);
        updateSetting(idx, { widget: newKey });
        onMessage({ type: 'info', message: 'Ключ виджета сгенерирован. Не забудьте сохранить.' });
    }

    async function refreshFull(skipLoadingState) {
        if (!uuid) return;
        try {
            if (!skipLoadingState) setIsFullLoading(true);
            const data = await new Api({ token }).getFullMerchant(uuid);
            if (!mounted.current) return;
            setFullMerchant(data);
            setEditFull(JSON.parse(JSON.stringify(data)));

            // Обновляем форму из полных данных мерчанта
            if (data.merchant) {
                setForm((prev) => ({
                    ...prev,
                    name: data.merchant.name || prev.name || '',
                    description: data.merchant.description || prev.description || '',
                    url: data.merchant.url || prev.url || '',
                    inn: data.merchant.inn || prev.inn || '',
                    kpp: data.merchant.kpp || prev.kpp || '',
                    ogrn: data.merchant.ogrn || prev.ogrn || '',
                    status: data.merchant.status != null ? data.merchant.status : prev.status,
                }));
            }
        } catch (e) {
            onMessage({ type: 'error', message: intl.formatMessage({ id: 'ProjectEditing.error.refresh' }) });
        } finally {
            if (mounted.current && !skipLoadingState) setIsFullLoading(false);
        }
    }

    const isProjectSelected = !!uuid && project && Object.keys(project).length > 0;

    const ef = editFull || {};
    const efMerchant = ef.merchant || null;
    const efProviders = Array.isArray(ef.providers) ? ef.providers : [];
    const efSettings = Array.isArray(ef.settings) ? ef.settings : [];

    const providersByUuid = efProviders.reduce((acc, p) => {
        if (p && p.uuid) acc[p.uuid] = p;
        return acc;
    }, {});

    const updateSettingFee = (index, feeKey, field, value) => {
        setEditFull((prev) => {
            const next = Object.assign({}, prev || {});
            const list = Array.isArray(next.settings) ? next.settings.slice() : [];
            const s = Object.assign({}, list[index] || {});
            const fee = Object.assign({}, s.fee || {});
            const sub = Object.assign({}, fee[feeKey] || {});
            sub[field] = value;
            fee[feeKey] = sub;
            s.fee = fee;
            list[index] = s;
            next.settings = list;
            return next;
        });
    };

    const onUsersChange = (user) => {
        setProjectUsers(user);
    };

    const onEditPermissions = (user) => {
        // Если юзер только добавлен в локальный список, но проект ещё не сохранён —
        // в БД ещё нет account_access_settings записи, и API get-permissions упадёт 404.
        // Просим сохранить проект сначала.
        const isPersistedUser =
            user && user.id && originalProjectUsers.some((u) => u && String(u.id) === String(user.id));
        if (!isPersistedUser) {
            onMessage({
                type: 'warning',
                message: 'Сначала сохраните проект, чтобы настроить права пользователя',
            });
            return;
        }
        setPermissionsModal({
            open: true,
            user: user,
        });
    };

    const addSettingFeeType = (index, feeKey) => {
        setEditFull((prev) => {
            const next = Object.assign({}, prev || {});
            const list = Array.isArray(next.settings) ? next.settings.slice() : [];
            const s = Object.assign({}, list[index] || {});
            const fee = Object.assign({}, s.fee || {});
            if (!fee[feeKey]) {
                fee[feeKey] = { cost: 0, fixedPerTransaction: 0, costFixedMinimum: 0 };
            }
            s.fee = fee;
            list[index] = s;
            next.settings = list;
            return next;
        });
    };

    const removeSetting = (index) => {
        setEditFull((prev) => {
            const next = Object.assign({}, prev || {});
            const list = Array.isArray(next.settings) ? next.settings.slice() : [];
            list.splice(index, 1);
            next.settings = list;
            return next;
        });
    };

    const addSetting = () => {
        setEditFull((prev) => {
            const next = Object.assign({}, prev || {});
            const list = Array.isArray(next.settings) ? next.settings.slice() : [];
            const firstProvider = efProviders.length > 0 ? efProviders[0].uuid : '';
            list.push({
                id: Date.now(),
                providerUuid: firstProvider,
                providerName:
                    firstProvider && providersByUuid[firstProvider] ? providersByUuid[firstProvider].name : '',
                terminalId: null,
                enabled: true,
                priority: 1,
                balance: 0,
                currency: { code: 'RUB', name: 'Russian Ruble' },
                fee: {},
                isSBP: false,
                isMP2P: false,
                preAuth: false,
                partialRefund: false,
                partialReversal: false,
                recurring: false,
                payoutBalanceCheck: false,
                transactionRules: [],
            });
            next.settings = list;
            return next;
        });
    };

    function currencyToStr(c) {
        if (!c) return '—';
        const name = c.name || '';
        const code = c.code || '';
        return `${name ? name : '—'}${code ? ` (${code})` : ''}`;
    }

    return (
        <Fragment>
            <div className={classes.Card}>
                {/* Оверлей блокировки при сохранении */}
                {isSaving && (
                    <div className={classes.SavingOverlay}>
                        <div className={classes.SavingSpinner}></div>
                        <span>Сохранение...</span>
                    </div>
                )}

                {isProjectSelected ? (
                    <Fragment>
                        {/* Лоадер при загрузке данных */}
                        {isFullLoading && (
                            <div className={classes.LoadingOverlay}>
                                <div className={classes.LoadingSpinner}></div>
                                <span>Загрузка данных...</span>
                            </div>
                        )}

                        <div className={classes.Content}>
                            <Status
                                currentStatus={form.status}
                                setCurrentStatus={setCurrentStatus}
                                isDisabled={isSaving || isFullLoading}
                            />

                            {/* Инфо */}
                            <dl className={classes.List}>
                                <dt>
                                    <FormattedMessage
                                        id="ProjectEditing.field.id"
                                        defaultMessage="Идентификатор"
                                    />
                                </dt>
                                <dd>{uuid}</dd>
                            </dl>

                            {/* Основные данные проекта */}
                            <div className={classes.ProjectFields}>
                                <InputHelper
                                    onChange={onChange}
                                    value={form.name}
                                    fieldName="name"
                                    label="ProjectEditing.field.name"
                                    isDisabled={isSaving || isFullLoading}
                                />
                                <InputHelper
                                    onChange={onChange}
                                    value={form.description}
                                    fieldName="description"
                                    label="ProjectEditing.field.description"
                                    isDisabled={isSaving || isFullLoading}
                                />
                                <InputHelper
                                    onChange={onChange}
                                    value={form.url}
                                    fieldName="url"
                                    label="ProjectEditing.field.url"
                                    isDisabled={isSaving || isFullLoading}
                                />
                                <InputHelper
                                    onChange={onChange}
                                    value={form.inn}
                                    fieldName="inn"
                                    label="ProjectEditing.field.inn"
                                    isDisabled={isSaving || isFullLoading}
                                />
                                <InputHelper
                                    onChange={onChange}
                                    value={form.kpp}
                                    fieldName="kpp"
                                    label="ProjectEditing.field.kpp"
                                    isDisabled={isSaving || isFullLoading}
                                />
                                <InputHelper
                                    onChange={onChange}
                                    value={form.ogrn}
                                    fieldName="ogrn"
                                    label="ProjectEditing.field.ogrn"
                                    isDisabled={isSaving || isFullLoading}
                                />
                            </div>

                            <div className={classes.Divider} />

                            {/* Настройки антифрода */}
                            <h4 className={classes.BlockTitle}>
                                <FormattedMessage id="ProjectEditing.antifraud.title" defaultMessage="Антифрод" />
                            </h4>
                            <div className={classes.ServiceCard}>
                                <label className={classes.AntifraudToggle}>
                                    <input
                                        type="checkbox"
                                        checked={isAntifraud}
                                        onChange={(e) => updateAntifraud(e.target.checked)}
                                    />
                                    <span className={classes.ToggleSwitch}></span>
                                    <span className={classes.ToggleText}>Включить антифрод</span>
                                </label>

                                {isAntifraud && (
                                    <div style={{ marginTop: 16 }}>
                                        <div className={classes.HintText} style={{ marginBottom: 12 }}>
                                            Настройте категории проверки для платежей и выплат
                                        </div>

                                        {/* Payment settings */}
                                        <div className={classes.SubBlock}>
                                            <b>Приём платежей:</b>
                                            <div className={classes.MethodsGrid} style={{ marginTop: 8 }}>
                                                <label className={classes.ToggleLabel}>
                                                    <input
                                                        type="checkbox"
                                                        checked={
                                                            antifraudSettings.payment &&
                                                            antifraudSettings.payment.database
                                                        }
                                                        onChange={(e) =>
                                                            updateAntifraudCategory(
                                                                'payment',
                                                                'database',
                                                                e.target.checked
                                                            )
                                                        }
                                                    />
                                                    <span className={classes.ToggleSwitch}></span>
                                                    <span className={classes.ToggleText}>По базе данных</span>
                                                </label>
                                                <label className={classes.ToggleLabel}>
                                                    <input
                                                        type="checkbox"
                                                        checked={
                                                            antifraudSettings.payment &&
                                                            antifraudSettings.payment.client
                                                        }
                                                        onChange={(e) =>
                                                            updateAntifraudCategory(
                                                                'payment',
                                                                'client',
                                                                e.target.checked
                                                            )
                                                        }
                                                    />
                                                    <span className={classes.ToggleSwitch}></span>
                                                    <span className={classes.ToggleText}>По клиенту</span>
                                                </label>
                                                <label className={classes.ToggleLabel}>
                                                    <input
                                                        type="checkbox"
                                                        checked={
                                                            antifraudSettings.payment &&
                                                            antifraudSettings.payment.scanfraud
                                                        }
                                                        onChange={(e) =>
                                                            updateAntifraudCategory(
                                                                'payment',
                                                                'scanfraud',
                                                                e.target.checked
                                                            )
                                                        }
                                                    />
                                                    <span className={classes.ToggleSwitch}></span>
                                                    <span className={classes.ToggleText}>Внешняя проверка</span>
                                                </label>
                                            </div>
                                        </div>

                                        {/* Payout settings */}
                                        <div className={classes.SubBlock}>
                                            <b>Выплаты:</b>
                                            <div className={classes.MethodsGrid} style={{ marginTop: 8 }}>
                                                <label className={classes.ToggleLabel}>
                                                    <input
                                                        type="checkbox"
                                                        checked={
                                                            antifraudSettings.payout &&
                                                            antifraudSettings.payout.database
                                                        }
                                                        onChange={(e) =>
                                                            updateAntifraudCategory(
                                                                'payout',
                                                                'database',
                                                                e.target.checked
                                                            )
                                                        }
                                                    />
                                                    <span className={classes.ToggleSwitch}></span>
                                                    <span className={classes.ToggleText}>По базе данных</span>
                                                </label>
                                                <label className={classes.ToggleLabel}>
                                                    <input
                                                        type="checkbox"
                                                        checked={
                                                            antifraudSettings.payout && antifraudSettings.payout.client
                                                        }
                                                        onChange={(e) =>
                                                            updateAntifraudCategory(
                                                                'payout',
                                                                'client',
                                                                e.target.checked
                                                            )
                                                        }
                                                    />
                                                    <span className={classes.ToggleSwitch}></span>
                                                    <span className={classes.ToggleText}>По клиенту</span>
                                                </label>
                                                <label className={classes.ToggleLabel}>
                                                    <input
                                                        type="checkbox"
                                                        checked={
                                                            antifraudSettings.payout &&
                                                            antifraudSettings.payout.scanfraud
                                                        }
                                                        onChange={(e) =>
                                                            updateAntifraudCategory(
                                                                'payout',
                                                                'scanfraud',
                                                                e.target.checked
                                                            )
                                                        }
                                                    />
                                                    <span className={classes.ToggleSwitch}></span>
                                                    <span className={classes.ToggleText}>Внешняя проверка</span>
                                                </label>
                                            </div>
                                        </div>

                                        <div style={{ marginTop: 16, paddingTop: 12, borderTop: '1px solid #eee' }}>
                                            <label className={classes.ToggleLabel}>
                                                <input
                                                    type="checkbox"
                                                    checked={antifraudCheckAccess}
                                                    onChange={(e) => setAntifraudCheckAccess(e.target.checked)}
                                                />
                                                <span className={classes.ToggleSwitch}></span>
                                                <span className={classes.ToggleText}>
                                                    Разрешить проверку антифрод (пользовательский раздел)
                                                </span>
                                            </label>
                                        </div>
                                    </div>
                                )}
                            </div>

                            <div className={classes.Divider} />

                            {/* Ключ мерчанта */}
                            <h4 className={classes.BlockTitle}>
                                <FormattedMessage
                                    id="ProjectEditing.merchantKey.title"
                                    defaultMessage="Ключ мерчанта"
                                />
                            </h4>
                            <div className={classes.ServiceCard} style={{ padding: 16 }}>
                                {isFullLoading ? (
                                    <FormattedMessage id="ProjectEditing.loading" defaultMessage="Загрузка..." />
                                ) : efMerchant ? (
                                    <div className={classes.MerchantKeyGroup}>
                                        <input
                                            type="text"
                                            readOnly
                                            value={(efMerchant && efMerchant.key) || ''}
                                            className={classes.MerchantKeyInput}
                                        />
                                        <button
                                            type="button"
                                            className={classes.BtnCopy}
                                            onClick={() => {
                                                navigator.clipboard.writeText((efMerchant && efMerchant.key) || '');
                                                onMessage({ type: 'success', message: 'Ключ скопирован' });
                                            }}
                                        >
                                            Копировать
                                        </button>
                                    </div>
                                ) : (
                                    <FormattedMessage id="ProjectEditing.noData" defaultMessage="Нет данных" />
                                )}
                                <div className={classes.HintText}>
                                    <FormattedMessage
                                        id="ProjectEditing.merchantKey.hint"
                                        defaultMessage="Этот ключ мерчант использует для подписи/аутентификации запросов."
                                    />
                                </div>
                            </div>

                            <div className={classes.Divider} />

                            {/* Платёжные настройки (включают настройки виджета для каждого MID) */}
                            <h4 className={classes.BlockTitle}>
                                Платёжные настройки
                                <span style={{ fontSize: 12, fontWeight: 400, color: '#6b7280', marginLeft: 8 }}>
                                    — основная настройка на странице{' '}
                                    <a href="/terminals-control" style={{ color: '#2563eb' }}>
                                        Терминалы
                                    </a>
                                </span>
                            </h4>

                            <div className={classes.ServicesWrapper}>
                                {isFullLoading && (
                                    <div className={classes.Empty}>
                                        <FormattedMessage id="ProjectEditing.loading" defaultMessage="Загрузка..." />
                                    </div>
                                )}
                                {!isFullLoading && efSettings.length === 0 && (
                                    <div className={classes.Empty}>
                                        <FormattedMessage
                                            id="ProjectEditing.settings.empty"
                                            defaultMessage="Настройки не найдены"
                                        />
                                    </div>
                                )}

                                {!isFullLoading &&
                                    efSettings.map((s, idx) => {
                                        const currentProvider =
                                            s && s.providerUuid && providersByUuid[s.providerUuid]
                                                ? providersByUuid[s.providerUuid]
                                                : null;
                                        const providerName = currentProvider
                                            ? currentProvider.name
                                            : (s && s.providerName) || '—';
                                        const currencyStr = currencyToStr(s && s.currency);

                                        return (
                                            <div key={(s && s.id) || `new-${idx}`} className={classes.ServiceCard}>
                                                <div
                                                    style={{
                                                        display: 'flex',
                                                        justifyContent: 'space-between',
                                                        alignItems: 'center',
                                                        marginBottom: 16,
                                                    }}
                                                >
                                                    <h5 style={{ margin: 0 }}>
                                                            <FormattedMessage
                                                                id="ProjectEditing.mid.title"
                                                                values={{
                                                                    id:
                                                                        (s && s.id) ||
                                                                        intl.formatMessage({
                                                                            id: 'ProjectEditing.mid.new',
                                                                        }),
                                                            }}
                                                            defaultMessage="МИД #{id}"
                                                        />
                                                    </h5>
                                                    <div className={classes.CardActions}>
                                                        {s && s.terminalId && terminalIdToTerminal[s.terminalId] ? (
                                                            <button
                                                                className={classes.BtnSecondary}
                                                                onClick={() => {
                                                                    const term = terminalIdToTerminal[s.terminalId];
                                                                    if (term) {
                                                                        setTerminalModal({
                                                                            open: true,
                                                                            terminal: term,
                                                                            settingIndex: idx,
                                                                        });
                                                                    }
                                                                }}
                                                            >
                                                                <FormattedMessage
                                                                    id="ProjectEditing.mid.openTerminal"
                                                                    defaultMessage="Открыть настройки терминала"
                                                                />
                                                            </button>
                                                        ) : (
                                                            <span
                                                                style={{
                                                                    color: '#999',
                                                                    fontSize: '0.875rem',
                                                                    padding: '0.5rem',
                                                                }}
                                                            >
                                                                <FormattedMessage
                                                                    id="ProjectEditing.mid.terminalNotLinked"
                                                                    defaultMessage="Терминал не привязан"
                                                                />
                                                            </span>
                                                        )}
                                                        <button
                                                            className={classes.BtnDanger}
                                                            onClick={() => removeSetting(idx)}
                                                            title="Удалить настройку"
                                                        >
                                                            <FormattedMessage
                                                                id="ProjectEditing.mid.delete"
                                                                defaultMessage="Удалить"
                                                            />
                                                        </button>
                                                    </div>
                                                </div>

                                                <div className={classes.ProviderSection}>
                                                    <span className={classes.ProviderLabel}>
                                                        <FormattedMessage
                                                            id="ProjectEditing.mid.provider"
                                                            defaultMessage="Провайдер:"
                                                        />
                                                    </span>
                                                    <select
                                                        className={classes.StyledSelect}
                                                        value={(s && s.providerUuid) || ''}
                                                        onChange={(e) => {
                                                            const pu = e.target.value;
                                                            updateSetting(idx, {
                                                                providerUuid: pu,
                                                                providerName: providersByUuid[pu]
                                                                    ? providersByUuid[pu].name
                                                                    : '',
                                                            });
                                                        }}
                                                    >
                                                        {efProviders.length === 0 && (
                                                            <option value="">— нет провайдеров —</option>
                                                        )}
                                                        {efProviders.map((p) => (
                                                            <option key={p.uuid} value={p.uuid}>
                                                                {p.name}
                                                            </option>
                                                        ))}
                                                    </select>
                                                    <div className={classes.ProviderCurrent}>
                                                        <FormattedMessage
                                                            id="ProjectEditing.mid.currentProvider"
                                                            defaultMessage="Текущий провайдер:"
                                                        />{' '}
                                                        <b>{providerName}</b>
                                                    </div>
                                                </div>

                                                <div className={classes.SubBlock}>
                                                    <b>
                                                        <FormattedMessage
                                                            id="ProjectEditing.mid.currencyBalance"
                                                            defaultMessage="Валюта и баланс:"
                                                        />
                                                    </b>
                                                    <div style={{ marginTop: 6 }}>
                                                        <FormattedMessage
                                                            id="ProjectEditing.mid.currency"
                                                            defaultMessage="Валюта:"
                                                        />{' '}
                                                        {currencyStr}
                                                        <br />
                                                        <FormattedMessage
                                                            id="ProjectEditing.mid.balance"
                                                            defaultMessage="Баланс:"
                                                        />{' '}
                                                        {s && s.balance}
                                                    </div>
                                                </div>

                                                <div className={classes.SubBlock}>
                                                    <b>
                                                        <FormattedMessage
                                                            id="ProjectEditing.mid.methods"
                                                            defaultMessage="Доступные методы:"
                                                        />
                                                    </b>
                                                    <div className={classes.MethodsGrid}>
                                                        <label className={classes.ToggleLabel}>
                                                            <input
                                                                type="checkbox"
                                                                checked={!!s.isSBP}
                                                                onChange={(e) =>
                                                                    updateSetting(idx, { isSBP: e.target.checked })
                                                                }
                                                            />
                                                            <span className={classes.ToggleSwitch}></span>
                                                            <span className={classes.ToggleText}>СБП</span>
                                                        </label>
                                                        <label className={classes.ToggleLabel}>
                                                            <input
                                                                type="checkbox"
                                                                checked={!!s.isMP2P}
                                                                onChange={(e) =>
                                                                    updateSetting(idx, { isMP2P: e.target.checked })
                                                                }
                                                            />
                                                            <span className={classes.ToggleSwitch}></span>
                                                            <span className={classes.ToggleText}>Р2Р</span>
                                                        </label>
                                                        <label className={classes.ToggleLabel}>
                                                            <input
                                                                type="checkbox"
                                                                checked={!!s.preAuth}
                                                                onChange={(e) =>
                                                                    updateSetting(idx, { preAuth: e.target.checked })
                                                                }
                                                            />
                                                            <span className={classes.ToggleSwitch}></span>
                                                            <span className={classes.ToggleText}>Предавторизация</span>
                                                        </label>
                                                        <label className={classes.ToggleLabel}>
                                                            <input
                                                                type="checkbox"
                                                                checked={!!s.partialRefund}
                                                                onChange={(e) =>
                                                                    updateSetting(idx, {
                                                                        partialRefund: e.target.checked,
                                                                    })
                                                                }
                                                            />
                                                            <span className={classes.ToggleSwitch}></span>
                                                            <span className={classes.ToggleText}>
                                                                Частичный возврат
                                                            </span>
                                                        </label>
                                                        <label className={classes.ToggleLabel}>
                                                            <input
                                                                type="checkbox"
                                                                checked={!!s.partialReversal}
                                                                onChange={(e) =>
                                                                    updateSetting(idx, {
                                                                        partialReversal: e.target.checked,
                                                                    })
                                                                }
                                                            />
                                                            <span className={classes.ToggleSwitch}></span>
                                                            <span className={classes.ToggleText}>Частичная отмена</span>
                                                        </label>
                                                        <label className={classes.ToggleLabel}>
                                                            <input
                                                                type="checkbox"
                                                                checked={!!s.recurring}
                                                                onChange={(e) =>
                                                                    updateSetting(idx, { recurring: e.target.checked })
                                                                }
                                                            />
                                                            <span className={classes.ToggleSwitch}></span>
                                                            <span className={classes.ToggleText}>Рекуррент</span>
                                                        </label>
                                                        <label className={classes.ToggleLabel}>
                                                            <input
                                                                type="checkbox"
                                                                checked={!!s.payoutBalanceCheck}
                                                                onChange={(e) =>
                                                                    updateSetting(idx, {
                                                                        payoutBalanceCheck: e.target.checked,
                                                                    })
                                                                }
                                                            />
                                                            <span className={classes.ToggleSwitch}></span>
                                                            <span className={classes.ToggleText}>Проверка баланса</span>
                                                        </label>
                                                    </div>
                                                </div>

                                                <div className={classes.SubBlock}>
                                                    <b>
                                                        <FormattedMessage
                                                            id="ProjectEditing.mid.fees"
                                                            defaultMessage="Комиссии:"
                                                        />
                                                    </b>
                                                    <div style={{ marginTop: 8 }}>
                                                        {(() => {
                                                            // Для UnlimcoBank-терминала показываем раздельные комиссии
                                                            // payout_card / payout_sbp вместо одной payout.
                                                            const term =
                                                                s && s.terminalId
                                                                    ? terminalIdToTerminal[s.terminalId]
                                                                    : null;
                                                            const isUnlimcoMs =
                                                                term && term.processingProtocol === 'UnlimcoBank';
                                                            const feeKeys = isUnlimcoMs
                                                                ? ['payment', 'payout_card', 'payout_sbp']
                                                                : ['payment', 'payout'];
                                                            return feeKeys;
                                                        })().map((feeKey) => {
                                                            const fee =
                                                                s && s.fee && s.fee[feeKey] ? s.fee[feeKey] : null;
                                                            const feeLabel = (function () {
                                                                if (feeKey === 'payment')
                                                                    return (
                                                                        <FormattedMessage
                                                                            id="ProjectEditing.mid.fee.payment"
                                                                            defaultMessage="Платёж"
                                                                        />
                                                                    );
                                                                if (feeKey === 'payout_card')
                                                                    return (
                                                                        <FormattedMessage
                                                                            id="ProjectEditing.mid.fee.payout_card"
                                                                        defaultMessage="Выплата на карту"
                                                                        />
                                                                    );
                                                                if (feeKey === 'payout_sbp')
                                                                    return (
                                                                        <FormattedMessage
                                                                            id="ProjectEditing.mid.fee.payout_sbp"
                                                                        defaultMessage="Выплата по СБП"
                                                                        />
                                                                    );
                                                                return (
                                                                    <FormattedMessage
                                                                        id="ProjectEditing.mid.fee.payout"
                                                                        defaultMessage="Выплата"
                                                                    />
                                                                );
                                                            })();
                                                            return (
                                                                <div key={feeKey} className={classes.FeeCard}>
                                                                    <div className={classes.FeeInputs}>
                                                                        <span className={classes.FeeCardTitle}>
                                                                            {feeLabel}
                                                                        </span>
                                                                        {fee ? (
                                                                            <Fragment>
                                                                                <div className={classes.FeeInputGroup}>
                                                                                    <label>
                                                                                        <FormattedMessage
                                                                                            id="ProjectEditing.mid.fee.percent"
                                                                                            defaultMessage="%:"
                                                                                        />
                                                                                    </label>
                                                                                    <input
                                                                                        type="number"
                                                                                        step="0.01"
                                                                                        className={classes.StyledInput}
                                                                                        value={
                                                                                            fee.cost != null
                                                                                                ? fee.cost
                                                                                                : 0
                                                                                        }
                                                                                        onChange={(e) =>
                                                                                            updateSettingFee(
                                                                                                idx,
                                                                                                feeKey,
                                                                                                'cost',
                                                                                                parseFloat(
                                                                                                    e.target.value ||
                                                                                                        '0'
                                                                                                )
                                                                                            )
                                                                                        }
                                                                                    />
                                                                                </div>
                                                                                <div className={classes.FeeInputGroup}>
                                                                                    <label>
                                                                                        <FormattedMessage
                                                                                            id="ProjectEditing.mid.fee.fixed"
                                                                                            defaultMessage="Фикс.:"
                                                                                        />
                                                                                    </label>
                                                                                    <input
                                                                                        type="number"
                                                                                        step="0.01"
                                                                                        className={classes.StyledInput}
                                                                                        value={
                                                                                            fee.fixedPerTransaction !=
                                                                                            null
                                                                                                ? fee.fixedPerTransaction
                                                                                                : 0
                                                                                        }
                                                                                        onChange={(e) =>
                                                                                            updateSettingFee(
                                                                                                idx,
                                                                                                feeKey,
                                                                                                'fixedPerTransaction',
                                                                                                parseFloat(
                                                                                                    e.target.value ||
                                                                                                        '0'
                                                                                                )
                                                                                            )
                                                                                        }
                                                                                    />
                                                                                </div>
                                                                                <div className={classes.FeeInputGroup}>
                                                                                    <label>
                                                                                        <FormattedMessage
                                                                                            id="ProjectEditing.mid.fee.minFixed"
                                                                                            defaultMessage="Мин. фикс.:"
                                                                                        />
                                                                                    </label>
                                                                                    <input
                                                                                        type="number"
                                                                                        step="0.01"
                                                                                        className={classes.StyledInput}
                                                                                        value={
                                                                                            fee.costFixedMinimum != null
                                                                                                ? fee.costFixedMinimum
                                                                                                : 0
                                                                                        }
                                                                                        onChange={(e) =>
                                                                                            updateSettingFee(
                                                                                                idx,
                                                                                                feeKey,
                                                                                                'costFixedMinimum',
                                                                                                parseFloat(
                                                                                                    e.target.value ||
                                                                                                        '0'
                                                                                                )
                                                                                            )
                                                                                        }
                                                                                    />
                                                                                </div>
                                                                            </Fragment>
                                                                        ) : (
                                                                            <button
                                                                                className={classes.BtnSecondary}
                                                                                onClick={() =>
                                                                                    addSettingFeeType(idx, feeKey)
                                                                                }
                                                                            >
                                                                                <FormattedMessage
                                                                                    id="ProjectEditing.mid.fee.add"
                                                                                    defaultMessage="Добавить комиссию"
                                                                                />
                                                                            </button>
                                                                        )}
                                                                    </div>
                                                                </div>
                                                            );
                                                        })}
                                                    </div>
                                                </div>

                                                {/* Настройки виджета для этого MID */}
                                                <div className={classes.SubBlock}>
                                                    <b>
                                                        <FormattedMessage
                                                            id="ProjectEditing.mid.widget"
                                                            defaultMessage="Настройки виджета:"
                                                        />
                                                    </b>
                                                    <div style={{ marginTop: 8 }}>
                                                        <div className={classes.WidgetField}>
                                                            <label className={classes.WidgetLabel}>
                                                                <FormattedMessage
                                                                    id="ProjectEditing.widget.key"
                                                                    defaultMessage="Ключ виджета"
                                                                />
                                                            </label>
                                                            <div className={classes.WidgetInputGroup}>
                                                                <input
                                                                    type="text"
                                                                    value={(s && s.widget) || ''}
                                                                    onChange={(e) =>
                                                                        updateSetting(idx, { widget: e.target.value })
                                                                    }
                                                                    className={classes.WidgetInput}
                                                                    placeholder="Введите или сгенерируйте ключ"
                                                                />
                                                                <button
                                                                    type="button"
                                                                    className={classes.WidgetBtnGenerate}
                                                                    onClick={() => generateWidgetKeyForSetting(idx)}
                                                                >
                                                                    <FormattedMessage
                                                                        id="ProjectEditing.widget.generate"
                                                                        defaultMessage="Сгенерировать"
                                                                    />
                                                                </button>
                                                            </div>
                                                            <div className={classes.HintText}>
                                                                <FormattedMessage
                                                                    id="ProjectEditing.widget.keyHint"
                                                                    defaultMessage="Ключ для авторизации виджета"
                                                                />
                                                            </div>
                                                        </div>
                                                        <div className={classes.WidgetField}>
                                                            <label className={classes.WidgetLabel}>
                                                                <FormattedMessage
                                                                    id="ProjectEditing.widget.callback"
                                                                    defaultMessage="Адрес колбека"
                                                                />
                                                            </label>
                                                            <input
                                                                type="text"
                                                                value={(s && s.widgetCallback) || ''}
                                                                onChange={(e) =>
                                                                    updateSetting(idx, {
                                                                        widgetCallback: e.target.value,
                                                                    })
                                                                }
                                                                className={classes.WidgetInput}
                                                                placeholder="https://example.com/callback"
                                                            />
                                                            <div className={classes.HintText}>
                                                                <FormattedMessage
                                                                    id="ProjectEditing.widget.callbackHint"
                                                                    defaultMessage="Адрес для отправки колбеков от виджета"
                                                                />
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        );
                                    })}

                                {!isFullLoading && (
                                    <div style={{ marginTop: 16, marginBottom: 8 }}>
                                        <button className={classes.BtnSuccess} onClick={addSetting}>
                                            + Добавить платёжную настройку
                                        </button>
                                    </div>
                                )}

                                <div className={classes.Divider} />

                                <div className={classes.Block}>
                                    <div className={classes.Col_1}>
                                        <h4 className={classes.BlockTitle}>
                                            <FormattedMessage
                                                id="ProjectEditing.users.title"
                                                defaultMessage="Пользователи проекта"
                                            />
                                        </h4>
                                    </div>
                                    <div className={classes.ColFullWidth}>
                                        <UserSearchList
                                            isLoading={isFullLoading}
                                            token={token}
                                            usersList={usersList}
                                            users={projectUsers}
                                            onUsersChange={onUsersChange}
                                            onEditPermissions={onEditPermissions}
                                        />
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Footer */}
                        <div className={classes.CardFooter}>
                            <button
                                className={classes.BtnPrimary}
                                onClick={saveAll}
                                disabled={isSaving || isFullLoading}
                            >
                                {isSaving ? 'Сохранение...' : 'Сохранить'}
                            </button>
                        </div>
                    </Fragment>
                ) : (
                    <div className={classes.Content}>
                        <div className={classes.Empty}>
                            <h5>
                                <FormattedMessage id="ProjectEditing.empty" defaultMessage="Проект не выбран" />
                            </h5>
                        </div>
                    </div>
                )}
            </div>

            {terminalModal.open && (
                <div className={classes.ModalOverlay} onClick={() => setTerminalModal({ open: false, terminal: null })}>
                    <div className={classes.Modal} onClick={(e) => e.stopPropagation()}>
                        <div className={classes.ModalHeader}>
                            <h4>
                                <FormattedMessage
                                    id="ProjectEditing.modal.terminalSettings"
                                    defaultMessage="Настройки терминала"
                                />
                            </h4>
                            <button
                                className={classes.ModalClose}
                                onClick={() => setTerminalModal({ open: false, terminal: null })}
                            >
                                ×
                            </button>
                        </div>
                        <div className={classes.ModalBody}>
                            <TerminalEditing
                                terminal={terminalModal.terminal}
                                token={token}
                                onSaved={() => {
                                    refreshFull();
                                    setTerminalModal((prev) => ({ ...prev, open: false }));
                                }}
                            />
                        </div>
                    </div>
                </div>
            )}

            {permissionsModal.open && permissionsModal.user && (
                <div className={classes.ModalOverlay} onClick={() => setPermissionsModal({ open: false, user: null })}>
                    <div className={classes.Modal} onClick={(e) => e.stopPropagation()}>
                        <div className={classes.ModalHeader}>
                            <h4>
                                <FormattedMessage
                                    id="ProjectEditing.modal.userPermissions"
                                    defaultMessage="Права пользователя {username}"
                                    values={{ username: permissionsModal.user.username || '' }}
                                />
                            </h4>
                            <button
                                className={classes.ModalClose}
                                onClick={() => setPermissionsModal({ open: false, user: null })}
                            >
                                ×
                            </button>
                        </div>
                        <div className={classes.ModalBody}>
                            <PermissionsEditor
                                token={token}
                                accountId={permissionsModal.user.id}
                                merchantUuid={uuid}
                                onClose={() => setPermissionsModal({ open: false, user: null })}
                                onSaved={() => {
                                    setPermissionsModal({ open: false, user: null });
                                }}
                            />
                        </div>
                    </div>
                </div>
            )}
        </Fragment>
    );
}

export default ProjectEditing;



// WEBPACK FOOTER //
// ./src/Components/ComponentsOfAdministrator/ProjectEditing/index.js